import { JSX } from 'react';
import {
  StepBackwardOutlined,
  StepForwardOutlined,
  CaretRightOutlined,
  PauseOutlined,
} from '@ant-design/icons';
import type { AnnotatedFrame } from '../ViewerPane/hooks/useAnnotatedFrames';
import type { PhaseKeyframe } from '../ViewerPane/hooks/usePhaseKeyframes';
import { Scrubber } from './Scrubber';
import styles from './FrameSlider.module.css';

interface Props {
  isPlaying: boolean;
  onTogglePlayPause: () => void;
  frameCount: number;
  currentFrame: number;
  onFrameChange: (frameIndex: number) => void;
  onPreviousFrame: () => void;
  onNextFrame: () => void;
  /** Frames carrying an annotation — drawn as markers on the scrubber. */
  annotatedFrames?: AnnotatedFrame[];
  /** ED/ES keyframes — drawn as labelled markers on the scrubber (AP-909). */
  phaseKeyframes?: PhaseKeyframe[];
}

const PHASE_NAME = { ED: 'End diastole', ES: 'End systole' } as const;

export function FrameSlider({
  isPlaying,
  onTogglePlayPause,
  frameCount,
  currentFrame,
  onFrameChange,
  onPreviousFrame,
  onNextFrame,
  annotatedFrames = [],
  phaseKeyframes = [],
}: Props): JSX.Element {
  const markers = [
    ...annotatedFrames.map(({ frame, kind }) => ({
      step: frame,
      kind,
      label: `${kind === 'ai' ? 'AI annotation' : 'Manual annotation'} on frame ${frame + 1}`,
    })),
    // After annotations so a phase tick draws on top of one on the same frame.
    ...phaseKeyframes.map(({ frame, phase }) => ({
      step: frame,
      kind: phase.toLowerCase(),
      label: `${PHASE_NAME[phase]} (${phase}) on frame ${frame + 1}`,
      text: phase,
    })),
  ];

  // Jumping to a marked frame is for inspecting it, so stop playback.
  const handleMarkerClick = (frame: number) => {
    if (isPlaying) onTogglePlayPause();
    onFrameChange(frame);
  };

  return (
    <div className={styles.wrapper} data-frame-slider>
      {/* Previous Frame */}
      <button
        type="button"
        className={styles.frameButton}
        onClick={onPreviousFrame}
        title="Previous frame"
      >
        <StepBackwardOutlined />
      </button>

      <button
        type="button"
        className={styles.frameButton}
        onClick={onTogglePlayPause}
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
      </button>

      {/* Next Frame */}
      <button
        type="button"
        className={styles.frameButton}
        onClick={onNextFrame}
        title="Next frame"
      >
        <StepForwardOutlined />
      </button>
      <Scrubber
        stepCount={frameCount}
        value={currentFrame}
        onChange={onFrameChange}
        markers={markers}
        onMarkerClick={handleMarkerClick}
      />

      <div className={styles.frameInfo}>
        <span className={styles.frameLabel}>Frame</span>{' '}
        {/* Reserve the width of the largest frame number so the label — and
            with it the scrubber — never resizes as the digits change. */}
        <span
          className={styles.frameNumber}
          style={{ minWidth: `${String(frameCount).length}ch` }}
        >
          {currentFrame + 1}
        </span>{' '}
        / {frameCount}
      </div>
    </div>
  );
}
